import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotificationSettings,
  NotificationRecipientRule,
  NotificationRuleOperator,
  saveNotificationSettings
} from "../../api/admin";
import { usePermissions } from "../../auth/permissions";
import { formConfig } from "../../formConfig";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RULE_OPERATORS: Array<{ value: NotificationRuleOperator; label: string; needsValue?: boolean }> = [
  { value: "equals", label: "equals", needsValue: true },
  { value: "notEquals", label: "does not equal", needsValue: true },
  { value: "in", label: "is one of", needsValue: true },
  { value: "notIn", label: "is not one of", needsValue: true },
  { value: "contains", label: "contains", needsValue: true },
  { value: "doesNotContain", label: "does not contain", needsValue: true },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" }
];

function splitAddresses(value: string): string[] {
  return Array.from(new Set(value.split(/[\n,;]/).map((address) => address.trim().toLowerCase()).filter(Boolean)));
}

function AddressList({
  label,
  values,
  onChange,
  emptyText
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  emptyText: string;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const address = text.trim().toLowerCase();
    if (!address) return;
    if (!EMAIL_RE.test(address)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!values.includes(address)) onChange([...values, address]);
    setText("");
    setError(null);
  };

  return (
    <section className="email-default-addresses">
      <label className="field-label">{label}</label>
      {values.length === 0 ? (
        <p className="hint">{emptyText}</p>
      ) : (
        <div className="recipient-chips">
          {values.map((address) => (
            <span key={address} className="recipient-chip">
              {address}
              <button type="button" onClick={() => onChange(values.filter((value) => value !== address))} aria-label={`Remove ${address}`}>x</button>
            </span>
          ))}
        </div>
      )}
      <div className="actions" style={{ marginTop: "0.5rem" }}>
        <input
          type="email"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          placeholder="name@brinkmannconstructors.com"
          style={{ flex: "1 1 18rem" }}
        />
        <button type="button" className="btn-secondary" onClick={add}>Add</button>
      </div>
      {error && <div className="banner-error">{error}</div>}
    </section>
  );
}

export default function NotificationRecipientsPage() {
  const navigate = useNavigate();
  const perms = usePermissions();
  const [from, setFrom] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [rules, setRules] = useState<NotificationRecipientRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!perms.isSuper) return;
    getNotificationSettings()
      .then((settings) => {
        setFrom(settings.from);
        setRecipients(settings.recipients);
        setCc(settings.cc);
        setRules(settings.rules);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [perms.isSuper]);

  if (perms.loading) return <div className="hint">Loading...</div>;
  if (!perms.isSuper) return <div className="banner-error">Only super admins can change notifications.</div>;

  const save = async () => {
    setError(null);
    if (!EMAIL_RE.test(from.trim())) {
      setError("Enter a valid From address.");
      return;
    }
    for (const rule of rules) {
      if (!rule.fieldId) {
        setError("Choose a form question for every conditional recipient rule.");
        return;
      }
      const operator = RULE_OPERATORS.find((item) => item.value === rule.operator);
      if (operator?.needsValue && !rule.value?.trim()) {
        setError("Enter a comparison value for every conditional recipient rule.");
        return;
      }
      if (rule.recipients.length === 0 && rule.cc.length === 0) {
        setError("Add at least one To or Cc address to every conditional recipient rule.");
        return;
      }
      if ([...rule.recipients, ...rule.cc].some((address) => !EMAIL_RE.test(address))) {
        setError("Conditional recipient rules must contain valid email addresses.");
        return;
      }
    }
    setSaving(true);
    try {
      const saved = await saveNotificationSettings({ from: from.trim(), recipients, cc, rules });
      setFrom(saved.from);
      setRecipients(saved.recipients);
      setCc(saved.cc);
      setRules(saved.rules);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const fields = formConfig.flatMap((section) => section.fields).filter((field) => field.question.trim());
  const updateRule = (id: string, patch: Partial<NotificationRecipientRule>) => {
    setRules((current) => current.map((rule) => rule.id === id ? { ...rule, ...patch } : rule));
  };
  const addRule = () => {
    setRules((current) => [...current, {
      id: `rule_${Date.now()}`,
      fieldId: fields[0]?.id ?? "",
      operator: "equals",
      value: "",
      recipients: [],
      cc: []
    }]);
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>Notifications</h1>
        <button type="button" className="btn-secondary" onClick={() => navigate("/admin")}>Done</button>
      </div>
      <p className="hint">Submitting a form does not send email. These defaults and rules are used only when an admin explicitly emails a submission.</p>
      {error && <div className="banner-error">{error}</div>}
      <div className="card">
        {loading ? (
          <div className="hint">Loading...</div>
        ) : (
          <>
            <label className="field-label">
              From
              <input type="email" value={from} onChange={(event) => setFrom(event.target.value)} style={{ width: "100%" }} />
            </label>
            <p className="hint">This sender address is used for every email.</p>
            <AddressList label="Always To" values={recipients} onChange={setRecipients} emptyText="No always-on recipients." />
            <AddressList label="Always Cc" values={cc} onChange={setCc} emptyText="No always-on Cc recipients." />
          </>
        )}
      </div>
      <div className="card">
        <div className="admin-page-head">
          <div>
            <h2 className="card-title">Conditional recipients</h2>
            <p className="hint">Matching rules add addresses to the always-on recipients. Separate multiple values for “is one of” with commas.</p>
          </div>
          <button type="button" className="btn-secondary" onClick={addRule} disabled={loading || fields.length === 0}>Add rule</button>
        </div>
        {fields.length === 0 ? (
          <p className="hint">Create at least one form question before adding a conditional recipient rule.</p>
        ) : rules.length === 0 ? (
          <p className="hint">No conditional recipients configured.</p>
        ) : (
          <div className="email-rule-list">
            {rules.map((rule) => {
              const operator = RULE_OPERATORS.find((item) => item.value === rule.operator);
              return (
                <div key={rule.id} className="email-rule">
                  <div className="email-rule-condition">
                    <label>IF question
                      <select value={rule.fieldId} onChange={(event) => updateRule(rule.id, { fieldId: event.target.value })}>
                        <option value="">Choose a question</option>
                        {fields.map((field) => <option key={field.id} value={field.id}>{field.question}</option>)}
                      </select>
                    </label>
                    <label>Operator
                      <select value={rule.operator} onChange={(event) => updateRule(rule.id, { operator: event.target.value as NotificationRuleOperator })}>
                        {RULE_OPERATORS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </label>
                    <label className="grow">Value
                      <input
                        type="text"
                        value={rule.value ?? ""}
                        disabled={!operator?.needsValue}
                        placeholder={operator?.needsValue ? "Comparison value" : "No value needed"}
                        onChange={(event) => updateRule(rule.id, { value: event.target.value })}
                      />
                    </label>
                  </div>
                  <div className="email-rule-recipients">
                    <label className="grow">THEN To
                      <input type="text" value={rule.recipients.join(", ")} placeholder="name@brinkmannconstructors.com" onChange={(event) => updateRule(rule.id, { recipients: splitAddresses(event.target.value) })} />
                    </label>
                    <label className="grow">Cc
                      <input type="text" value={rule.cc.join(", ")} placeholder="name@brinkmannconstructors.com" onChange={(event) => updateRule(rule.id, { cc: splitAddresses(event.target.value) })} />
                    </label>
                    <button type="button" className="btn-link" onClick={() => setRules((current) => current.filter((item) => item.id !== rule.id))}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && (
          <div className="actions" style={{ marginTop: "1rem" }}>
            <button type="button" className="btn-save" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
            {savedAt && <span className="hint">Saved at {savedAt}</span>}
          </div>
        )}
      </div>
    </>
  );
}